import React, { FC, ReactNode } from 'react'
import Modal from 'react-modal'
import classNames from 'classnames'
import cx from 'classnames'
import { FormValidation, UiSchema } from '@rjsf/utils'
import { Dictionary } from '@feedyou/utils'

import { areFormDataEqual, confirmAboutChanges } from './utils'
import { GenericFormData } from './types'
import { SettingsPopupOpenSection } from '../../store/modals'
import { OnFormDataChange } from './hooks'
import { ModalHeader, modalStyle } from './styles'
import { Spacer } from '../design-system/Spacer'
import { SettingsPopupTab } from './SettingsPopupTab'
import { FormSection, JsonSection } from './SettingsPopupSections'

interface Props {
  schema: {}
  formData: object | undefined
  // Needs to have stable ref ⚠️
  initialFormData: object | undefined
  openSection: SettingsPopupOpenSection
  subset?: string
  className?: string
  saveButton?: string
  liveValidate?: boolean
  uiSchema: UiSchema | {}
  footerContent?: JSX.Element | boolean
  validationErrorsByField?: Dictionary<string, string> | undefined

  close: () => void
  onError?: () => void
  save: (formData: any) => void
  onChange?: OnFormDataChange<any>
  validateSchema?: (formData: any, errors: FormValidation) => FormValidation
  renderAboveForm?: ReactNode
  renderBelowForm?: ReactNode
  hideContent?: boolean
  hideFormEditor?: boolean
  hideJsonEditor?: boolean

  additionalSections?: PopupSection[]
  tabOrder?: Dictionary<string, number>
  // This is a hackish way to do this, but good enough for now.
  // Want to re-evaluate this once we have more custom tabs (non form||json).
  customTabNames?: { form?: string; json?: string }
}

type PopupSection = {
  tab: { section: SettingsPopupOpenSection; label: string }
  renderContent: React.FC<SettingsPopupProps>
}

/**
 * @deprecated - this is not a sinsible way to approach popups.
 * Create your own popups with <CustomPopup /> component, or preferably modal() function instead.
 *
 * Note: BotSettingsJsonEditor cannot work as a fully controlled component (see its code for more detail).
 * This is very relevant to the data-flow inside settings popup
 */
export const SettingsPopup: FC<Props> = props => {
  const {
    subset,
    formData,
    initialFormData,
    className,
    openSection,
    close,
    additionalSections,
    tabOrder = { form: 1, json: 2 },
    customTabNames,
    hideFormEditor,
    hideJsonEditor
  } = props

  if (!formData) {
    return null
  }

  const closeWithPossibleConfirm = () => {
    areFormDataEqual(formData, initialFormData) ? close() : confirmAboutChanges(close)
  }

  return (
    <Modal
      isOpen={true}
      style={modalStyle}
      onRequestClose={closeWithPossibleConfirm}
      className={classNames('e2e-botsettings-modal modal-default', cx(className, subset))}
    >
      <ModalHeader>
        <Spacer marginLeft="-10px" order={-1} />

        {!hideFormEditor && (
          <SettingsPopupTab
            section="form"
            label={customTabNames?.form ?? 'Form'}
            order={tabOrder['form']}
          />
        )}
        {!hideJsonEditor && (
          <SettingsPopupTab
            section="json"
            label={customTabNames?.json ?? 'JSON'}
            order={tabOrder['json']}
          />
        )}
        {additionalSections?.map(section => (
          <SettingsPopupTab
            {...section.tab}
            key={section.tab.section}
            order={tabOrder[section.tab.section]}
          />
        ))}
      </ModalHeader>

      {!hideJsonEditor && openSection === 'json' && (
        <JsonSection {...props} close={closeWithPossibleConfirm} />
      )}

      {!hideFormEditor && openSection === 'form' && (
        <FormSection {...props} close={closeWithPossibleConfirm} />
      )}

      {additionalSections
        ?.filter(section => section.tab.section === openSection)
        .map(section => section.renderContent(props))}
    </Modal>
  )
}

export type SettingsPopupProps = Props
